import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { capitalize } from 'lodash';
import { CATEGORY_TYPE, PLATFORM_TYPE } from '@src/constants/support';
import BadgeAvatar from '@src/components/BadgeAvatar';
import {
  MAX_BODY_CONTENT_HEIGHT,
  MIN_OTHER_ELEMENT_HEIGHT,
} from '@src/constants/request';
import { LANDING_PAGE_URL } from '@src/configs';
import RequestStatus from '@src/components/RequestStatus';
import Table from '@src/components/NewTable';
import Typography from '@src/new-components/Typography';
import { extractVoiceName } from '@src/services/voice';
import { VOICE_LEVEL } from '@src/constants/voice';
import { SERVICE_TYPE } from '@src/constants/apiApp';
import {
  StyleVoiceItem,
  StyledRequestTable,
} from '@src/containers/TTSNew/NewRequest/index.style';
import TitleRequest from './TitleRequest';
import Action from './Action';

const RequestTable = ({
  requests,
  loading,
  page,
  total,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  activeServiceTab,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const { languages } = useSelector((state) => state.language);
  const { user } = useSelector((state) => state.auth);

  const renderVoiceName = (voice) => {
    const { voiceName, voiceType } = extractVoiceName({
      name: voice?.name,
      provider: voice?.provider,
    });
    const smallImgSrc =
      voice?.language?.roundImage ||
      languages.find((lang) => lang?.value === voice?.languageCode)?.roundImage;

    const voiceStyles = [
      voiceType || t(voice?.styles?.[0]),
      voice?.level !== VOICE_LEVEL.BASIC && t(voice?.level),
      voice?.beta && capitalize(t('beta')),
    ]
      ?.filter(Boolean)
      .join(' • ');

    return (
      <StyleVoiceItem>
        <BadgeAvatar
          width={30}
          img={voice?.roundImage}
          type="image"
          smallImgWidth={10}
          smallImg={smallImgSrc}
        />
        <div>
          <Typography variant="body-14-medium" style={{ whiteSpace: 'nowrap' }}>
            {voiceName}
          </Typography>
          <div className="new-voice-style">{voiceStyles}</div>
        </div>
      </StyleVoiceItem>
    );
  };

  const isSTT = activeServiceTab === SERVICE_TYPE.STT;

  const handleOpenErrorReport = (request) => {
    const ticketMetadata = {
      requestId: request.id,
      voiceCode: request.voice?.code,
      userId: user?.id,
    };
    const base64TicketMetadata = btoa(JSON.stringify(ticketMetadata));
    const encodedTicketMetadata = encodeURIComponent(base64TicketMetadata);
    const category = isSTT ? CATEGORY_TYPE.OTHER : CATEGORY_TYPE.TTS;

    window.open(
      `${LANDING_PAGE_URL}/support?metadata=${encodedTicketMetadata}&platform=${PLATFORM_TYPE.DESKTOP_WEB}&category=${category}`,
      '_blank',
    );
  };

  const columns = [
    {
      field: 'fakeColumn',
      title: '',
      sortable: false,
      align: 'left',
      width: '0px',
      cellWidth: '0px',
    },
    {
      field: 'title',
      title: t('title'),
      sortable: false,
      align: 'left',
      width: isSTT ? '60%' : '45%',
      cellWidth: isSTT ? '60%' : '45%',
      render: (row) => (
        <TitleRequest
          requestId={row.id}
          audioType={row?.audioType}
          characters={row?.characters}
          createdAt={row.createdAt}
          credits={row?.credits}
          audioDurationSeconds={row?.billedSeconds}
          isSTT={isSTT}
        />
      ),
    },
    ...(isSTT
      ? []
      : [
          {
            field: 'voice',
            sortable: true,
            align: 'left',
            width: '20%',
            cellWidth: '20%',
            render: (row) => (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                {renderVoiceName(row.voice)}
              </div>
            ),
          },
        ]),
    {
      field: 'status',
      width: isSTT ? '40%' : '35%',
      cellWidth: isSTT ? '40%' : '35%',
      render: (row) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <>
            <RequestStatus useNewRequestUI request={row} />
            <Action
              request={row}
              playable={!isSTT}
              onOpenErrorReport={() => handleOpenErrorReport(row)}
              isDisabledErrorReport={row?.isDisabledErrorReport}
              isSTT={isSTT}
              hideEffectAudio
              onRefresh={onRefresh}
            />
          </>
        </div>
      ),
    },
  ];

  return (
    <StyledRequestTable>
      <Table
        useInfiniteScrollRequests={false}
        isExpand
        columns={columns}
        flickerBackground={false}
        data={requests}
        page={page}
        useNewRequestUI
        requestListHeight={224}
        total={total}
        onChangeRowPage={onChangeRowsPerPage}
        sort={null}
        setNumSelected={() => {}}
        loading={loading}
        dropTable={false}
        selection
        onChangeSort={() => {}}
        rowsPerPage={rowsPerPage}
        onChangePage={onChangePage}
        onRowClick={() => {}}
        selected={[]}
        onSelect={() => {}}
        minWidth="1000px"
        tableHeight={
          false ||
          `${Math.min(
            window.innerHeight / 2 - MIN_OTHER_ELEMENT_HEIGHT,
            MAX_BODY_CONTENT_HEIGHT,
          )}px`
        }
        isShowCheckbox={false}
        headerStyle={{ zIndex: 10 }}
        isStickyHeader
        detailsApp
      />
    </StyledRequestTable>
  );
};

export default RequestTable;
